"use client";

import React from "react";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Clock3, Flame, PhoneCall, ShieldCheck, Wrench, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const trustPoints = [{
  title: "Licensed & Insured",
  description: "State-certified Georgia master plumbers",
  icon: ShieldCheck
}, {
  title: "Fast Local Dispatch",
  description: "Units stationed across Jonesboro & Clayton County",
  icon: Clock3
}, {
  title: "All Makes & Models",
  description: "Tank, tankless, gas, and electric systems",
  icon: Wrench
}];
const serviceBadges = ["Emergency Leak Repair", "No Hot Water Diagnosis", "Tankless Conversions", "Same-Day Tank Replacement"];
export default function WaterHeaterHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="waterheaterhero" data-nav="dark" className="relative overflow-hidden bg-primary py-16 text-primary-foreground md:py-24 lg:py-28">
      {/* Decorative subtle ambient glows */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Main Hero Content */}
          <div className="flex flex-col items-start lg:col-span-7">
            {/* Status Eyebrow Badge */}
            <motion.div variants={itemVariants} className="mb-6">
              <Badge variant="secondary" className="gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent-foreground tracking-wide uppercase sm:text-sm">
                <span className="relative flex size-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                  <span className="relative inline-flex size-2 rounded-full bg-accent" />
                </span>
                24/7 Water Heater Emergency Response in Jonesboro
              </Badge>
            </motion.div>

            {/* H1 Heading */}
            <motion.h1 variants={itemVariants} className="font-default text-3xl font-extrabold tracking-tight text-primary-foreground sm:text-4xl md:text-5xl lg:text-6xl">
              Water Heater Services in Jonesboro, GA
            </motion.h1>

            {/* Lead Paragraph */}
            <motion.p variants={itemVariants} className="mt-6 max-w-2xl text-base text-primary-foreground/90 sm:text-lg md:text-xl font-normal leading-relaxed">
              From sudden cold showers and leaking pressure valves to total heating element failure, our certified technicians diagnose and resolve water heater breakdowns fast. We deliver prompt emergency repairs, complete tank replacements, and high-efficiency tankless installations tailored to Clayton County homes.
            </motion.p>

            {/* Quick Service Tags */}
            <motion.div variants={itemVariants} className="mt-6 flex flex-wrap gap-2.5">
              {serviceBadges.map((service, i) => <div key={service} data-index={i} className="inline-flex items-center gap-1.5 rounded-md border border-primary-foreground/15 bg-primary-foreground/10 px-3 py-1 text-xs font-medium text-primary-foreground backdrop-blur-xs">
                  <CheckCircle2 className="size-3.5 text-accent" />
                  <span>{service}</span>
                </div>)}
            </motion.div>

            {/* Signature Dual-Action Conversion Cluster */}
            <motion.div variants={itemVariants} className="mt-8 flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:items-center">
              {/* Direct Emergency Dial Button */}
              <Button asChild size="lg" className="h-14 rounded-full bg-accent px-8 text-base font-bold text-accent-foreground shadow-lg shadow-accent/20 transition-all hover:bg-accent/90 active:scale-98">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2.5">
                  <PhoneCall className="size-5 shrink-0" />
                  <span>{"Call (470) 847-9076"}</span>
                </a>
              </Button>

              {/* Secondary Request Service Link */}
              <Button asChild variant="secondary" size="lg" className="h-14 rounded-full border border-primary-foreground/20 bg-secondary px-7 text-base font-semibold text-secondary-foreground shadow-sm hover:bg-secondary/80 active:scale-98">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <Zap className="size-4 shrink-0 text-primary" />
                  <span>Request Service Online</span>
                </Link>
              </Button>
            </motion.div>

            {/* Fast Response Assurance */}
            <motion.p variants={itemVariants} className="mt-4 flex items-center gap-2 text-xs text-primary-foreground/75 sm:text-sm font-medium">
              <AlertCircle className="size-4 text-accent" />
              <span>Average response time under 45 minutes for emergency heating calls.</span>
            </motion.p>
          </div>

          {/* Right Visual / Proof Cluster Card */}
          <div className="lg:col-span-5">
            <motion.div variants={itemVariants} className="relative rounded-2xl border border-primary-foreground/15 bg-primary-foreground/5 p-6 backdrop-blur-md sm:p-8 shadow-2xl">
              {/* Card Header */}
              <div className="flex items-center justify-between border-b border-primary-foreground/15 pb-5">
                <div className="flex items-center gap-3">
                  <div className="flex size-11 items-center justify-center rounded-xl bg-accent text-accent-foreground shadow-sm">
                    <Flame className="size-6" />
                  </div>
                  <div>
                    <h3 className="font-default text-lg font-bold text-primary-foreground">
                      Jonesboro Plumber Dispatch
                    </h3>
                    <p className="text-xs text-primary-foreground/80">
                      Clayton County Service Hub
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="border-accent text-accent font-semibold text-xs">
                  Ready Now
                </Badge>
              </div>

              {/* Key Trust Pillars */}
              <div className="mt-6 space-y-4">
                {trustPoints.map((point, i) => {
                const Icon = point.icon;
                return <div key={point.title} data-index={i} className="flex items-start gap-3.5 rounded-xl border border-primary-foreground/10 bg-primary-foreground/5 p-3.5 transition-colors hover:bg-primary-foreground/10">
                      <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-foreground/10 text-accent">
                        <Icon className="size-4.5" />
                      </div>
                      <div>
                        <h4 className="font-default text-sm font-semibold text-primary-foreground">
                          {point.title}
                        </h4>
                        <p className="mt-0.5 text-xs text-primary-foreground/80 leading-relaxed">
                          {point.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>

              {/* Service Assurance Box */}
              <div className="mt-6 rounded-xl border border-accent/30 bg-accent/10 p-4 text-center">
                <p className="text-xs font-semibold text-accent-foreground">
                  Need an urgent replacement? We carry top brand equipment on service trucks for same-day installation.
                </p>
                <div className="mt-3">
                  <Button asChild size="sm" className="h-8 rounded-full bg-accent px-4 text-xs font-bold text-accent-foreground hover:bg-accent/90">
                    <Link to="/water-heater-replacement/">
                      Explore Replacement Options
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}